// App shell: floating brand + Lens trigger on top, orbital dock at the bottom, one routed view.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRuntime } from './core/runtime.tsx';
import { useRoute, navigate } from './core/router.ts';
import { SPACES, modulesOf, resolveModule, type SpaceId } from './core/registry.tsx';
import { ScreenBoundary } from './core/boundary.tsx';
import { pendingDecisions } from '@vero/intelligence';
import { Icon } from './core/icons.tsx';
import { Lens } from './modules/lens.tsx';
import { openers } from './modules/forms.tsx';
import { startFlow } from './modules/flow.tsx';
import { QuickAdd } from './modules/quickadd.tsx';
import { SyncBadge } from './modules/sync.tsx';
import { VeroMark, VeroWordmark } from './core/brand.tsx';
import { getSetting, lastAutomationDay, runAutomations, planGeneralRenewalTasks, createGeneralRenewalTasks } from '@vero/domain';
import { giTaskTitle } from './modules/gi-renewals.tsx';
import { syncService } from './core/sync-service.ts';
import { LicenceBadge, LicenceBanner } from './modules/account.tsx';
import { SaveBadge } from './modules/data.tsx';
import { Tour } from './core/tour.tsx';
import { BookingWatcher } from './modules/website.tsx';
import { VideoCallWatcher } from './modules/video.tsx';
import { NpsWatcher } from './modules/nps.tsx';
import { OutlookMailWatcher } from './modules/outlook-mail.tsx';
import { WhatsAppWatcher } from './modules/whatsapp.tsx';
import { ESignWatcher } from './modules/esign.tsx';
import { DesktopRemindersWatcher, DesktopUpdateWatcher } from './modules/desktop-app.tsx';
import { HelpButton, openHelp } from './modules/guide.tsx';
import { openAskHelp } from './modules/help-assistant.tsx';
import { useShown } from './modules/setup.tsx';
import { useStudioVisible, useStudioOnly, STUDIO_ONLY_MODULES } from './modules/studio.tsx';
import { ModuleGate } from './core/rights.tsx';
import { MaintenanceBanner } from './modules/maintenance.tsx';
import { UpdateNotes, useServedNotes } from './modules/whats-new.tsx';

export function App() {
  const rt = useRuntime();
  const route = useRoute();
  const mod = resolveModule(route);
  const t = rt.t;
  const urgent = rt.attention.filter((a) => a.severity === 'critical').length;
  const casesAlert = rt.attention.filter((a) => a.rule === 'case_stalled' || a.rule === 'case_missing').length;
  const decisions = useMemo(() => pendingDecisions(rt.view, rt.today).length, [rt.view, rt.today]);
  const serviceAlert = rt.attention.filter((a) => a.rule === 'claim_followup' || a.rule === 'service_open').length;
  const badge: Partial<Record<SpaceId, [number, string?]>> = { today: [urgent], cases: [casesAlert, 'var(--high)'], growth: [decisions, 'var(--s1)'], people: [serviceAlert, 'var(--high)'] };
  const space = mod.space;
  const shown = useShown();
  // requirement 10 (S4): a standalone studio subscription opens the studio; the CRM's spaces are not offered
  const studioOnly = useStudioOnly();
  useEffect(() => { if (studioOnly && !STUDIO_ONLY_MODULES.includes(mod.id)) navigate('/studio', { replace: true }); }, [studioOnly, mod.id]);
  const spaces = studioOnly ? SPACES.filter((sp) => sp.id === 'growth' || sp.id === 'system') : SPACES;
  // remember the last module used in each space so the dock returns you where you were
  useEffect(() => { LAST[space] = route.path; }, [space, route.path]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement)?.tagName ?? '') || (e.target as HTMLElement)?.isContentEditable;
      if ((e.key === '?' && !typing && !e.metaKey && !e.ctrlKey) || e.key === 'F1') { e.preventDefault(); if (!document.querySelector('.overlay')) openHelp(rt, mod.id); return; }
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) { e.preventDefault(); rt.open(<Lens />); }
      // 16 Oct 2026: «Ρωτήστε το AVRASTRO» — questions about the app, from its own help (Ctrl+J / ⌘J)
      if (e.key.toLowerCase() === 'j' && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) { e.preventDefault(); if (!document.querySelector('.overlay')) openAskHelp(rt); return; }
      if (!typing && !rt.layer && !document.querySelector('.overlay') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (e.key === 'n') { e.preventDefault(); openers(rt).note(route.parts[0] === 'people' ? route.parts[1] : null); }
        if (e.key === 'g') navigate('/now');
        if (e.key === 'f') { e.preventDefault(); startFlow(rt); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rt, route, mod.id]);

  useEffect(() => { document.title = `${t(mod.labelKey)} · AVRASTRO`; }, [mod, t]);

  // Daily automations (spec §110): only the designated runner device runs them, at most once a day,
  // and they only ever create tasks (idempotent keys make a second run harmless anyway).
  useEffect(() => {
    const v = rt.view;
    const device = syncService()?.deviceId ?? 'local';
    if (rt.ws.readOnlyReason || getSetting<string | null>(v, 'automation.runner', null) !== device) return;
    if (!v.rows('automation_rules').some((r) => r.enabled) || (lastAutomationDay(v) ?? '') >= rt.today) return;
    void rt.run((w, c) => runAutomations(w, c)).then((cs) => { const n = Number(cs?.summary ?? 0); if (n > 0) rt.toast(t('auto.ran', { count: n }), 'success'); });
  }, [rt.today]);
  // General-insurance renewal follow-ups (45/30/15 days, Settings): the same runner device, only when one is due;
  // task ids are deterministic, so another device or a second run never creates a duplicate.
  useEffect(() => {
    const v = rt.view;
    const device = syncService()?.deviceId ?? 'local';
    if (rt.ws.readOnlyReason || getSetting<string | null>(v, 'automation.runner', null) !== device) return;
    if (!planGeneralRenewalTasks(v, rt.today).length) return;
    void rt.run((w, c) => createGeneralRenewalTasks(w, c, giTaskTitle(t))).then((cs) => { const n = Number(cs?.summary ?? 0); if (n > 0) rt.toast(t('gi_renewals.tasks_done', { count: n }), 'success'); });
  }, [rt.today]);

  return (
    <>
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand glass" onClick={() => navigate('/now')} role="link" tabIndex={0} aria-label="AVRASTRO">
            <span className="brand-mark"><VeroMark size={22} /></span>
            <div className="brand-text"><VeroWordmark height={12} /><span>{t('app.tagline')}</span></div>
          </div>
          <button className="lens-trigger glass" onClick={() => rt.open(<Lens />)} data-testid="lens-trigger">
            <Icon name="search" /><span>{t('lens.placeholder')}</span><span className="kbd">⌘K</span>
          </button>
          <div className="top-actions">
            <HelpButton moduleId={mod.id} variant="top" />
            <button className="btn btn-icon" aria-label={`${t('askhelp.launcher')} (${t('askhelp.shortcut')})`} title={`${t('askhelp.launcher')} (${t('askhelp.shortcut')})`} onClick={() => openAskHelp(rt)} data-testid="ask-help-launcher"><Icon name="chat" /></button>
            <button className="btn btn-icon" aria-label={t('lens.cmd.toggle_theme')} onClick={() => rt.setPref('theme', document.documentElement.dataset.theme === 'light' ? 'dark' : 'light')}><Icon name={document.documentElement.dataset.theme === 'light' ? 'moon' : 'sun'} /></button>
            <button className="btn btn-flow" onClick={() => startFlow(rt)} data-testid="topbar-flow" title={`${t('flow.start')} (F)`}><Icon name="flow" /><span>{t('flow.title')}</span></button>
            <SaveBadge />
            <LicenceBadge />
            <SyncBadge />
            <button className="btn btn-primary" onClick={() => openers(rt).note(route.parts[0] === 'people' ? route.parts[1] : null)}><Icon name="note" /><span>{t('focus.quick.note')}</span></button>
            <QuickAdd variant="top" />
          </div>
        </div>
      </header>
      <main className="shell">
        <div className="content">
          <SpaceNav space={space} current={mod.id} />
          <LicenceBanner />
          <MaintenanceBanner />
          <UpdateBanner />
          <ScreenBoundary key={mod.id} module={mod.id} labels={{ title: t('errors.screen.title'), body: t('errors.screen.body'), retry: t('errors.screen.retry'), home: t('nav.today') }}>
            <ModuleGate moduleId={mod.id}>{mod.render(route)}</ModuleGate>
          </ScreenBoundary>
        </div>
      </main>
      <QuickAdd variant="fab" />
      <Tour />
      <BookingWatcher />
      <VideoCallWatcher />
      <NpsWatcher />
      <OutlookMailWatcher />
      <WhatsAppWatcher />
      <ESignWatcher />
      <DesktopRemindersWatcher />
      <DesktopUpdateWatcher />
      <nav className="dock" aria-label={t('space.label')}>
        <div className="dock-inner glass">
          {spaces.map((sp) => {
            const [n, color] = badge[sp.id] ?? [0];
            return (
              <button key={sp.id} className="dock-btn" aria-current={space === sp.id ? 'page' : undefined} onClick={() => navigate(LAST[sp.id] ?? (modulesOf(sp.id).find((m) => !m.hidden && shown(m.id)) ?? modulesOf(sp.id)[0]!).path)} data-testid={`space-${sp.id}`}>
                <Icon name={sp.icon} /><span>{t(sp.labelKey)}</span>
                {n > 0 && <b className="dock-badge" style={color ? { background: color } : undefined}>{n}</b>}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}

const LAST: Partial<Record<SpaceId, string>> = {};

/** A newer version of the hosted app has been downloaded: one tap loads it (never a reload on its own — nothing typed is lost). */
function UpdateBanner() {
  const rt = useRuntime();
  const [ready, setReady] = useState(() => !!(window as unknown as { __veroUpdate?: boolean }).__veroUpdate);
  useEffect(() => { const on = () => setReady(true); window.addEventListener('vero:update-ready', on); return () => window.removeEventListener('vero:update-ready', on); }, []);
  // requirement 20: what the downloaded version brings, by plan (the platform that serves the web app names its version)
  const served = useServedNotes(ready);
  // the new version is applied by itself as soon as nothing is being typed or edited (no open dialog, no focused field):
  // an advisor who never presses «Ανανέωση» no longer stays on an old version for days
  useEffect(() => {
    if (!ready) return;
    const busy = () => !!document.querySelector('.overlay, [role="dialog"]') || /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName ?? '') || (document.activeElement as HTMLElement | null)?.isContentEditable === true;
    const i = setInterval(() => { if (!busy()) location.reload(); }, 20_000);
    return () => clearInterval(i);
  }, [ready]);
  if (!ready) return null;
  return (
    <div className="callout update-banner" role="status" data-testid="update-banner">
      <Icon name="spark" size={16} /><b>{rt.t('app.update_ready')}</b>
      <button className="btn btn-sm btn-primary" onClick={() => location.reload()}>{rt.t('app.update_reload')}</button>
      {served && <div style={{ flexBasis: '100%' }}><UpdateNotes target={served.version} notes={served.notes} /></div>}
    </div>
  );
}

/** The tools of the current space — one row, always in the same place. */
function SpaceNav({ space, current }: { space: SpaceId; current: string }) {
  const rt = useRuntime();
  const shown = useShown();
  // tools the advisor chose to hide (and those of a licence class they do not hold) leave the row — the current one stays
  const studio = useStudioVisible();
  const studioOnly = useStudioOnly();
  const mods = modulesOf(space).filter((m) => m.id === current || (!m.hidden && shown(m.id) && (m.visibleWith !== 'studio' || studio) && (!studioOnly || STUDIO_ONLY_MODULES.includes(m.id))));
  const sp = SPACES.find((x) => x.id === space)!;
  // when a space has more tools than fit, fade the cut edge and keep the current tool in view
  const tabs = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = tabs.current; if (!el) return;
    const mark = () => { const rtl = getComputedStyle(el).direction === 'rtl'; const x = Math.abs(el.scrollLeft); const over = el.scrollWidth - el.clientWidth > 2;
      el.classList.toggle(rtl ? 'fade-end' : 'fade-start', over && x > 2); el.classList.toggle(rtl ? 'fade-start' : 'fade-end', over && x < el.scrollWidth - el.clientWidth - 2); };
    el.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
    mark(); el.addEventListener('scroll', mark, { passive: true }); window.addEventListener('resize', mark);
    return () => { el.removeEventListener('scroll', mark); window.removeEventListener('resize', mark); };
  }, [space, current]);
  return (
    <nav className="spacenav" aria-label={rt.t(sp.labelKey)}>
      <span className="spacenav-title"><Icon name={sp.icon} size={16} />{rt.t(sp.labelKey)}</span>
      <div className="spacenav-tabs" role="tablist" ref={tabs}>
        {mods.map((m) => (
          <button key={m.id} role="tab" aria-selected={m.id === current} onClick={() => navigate(m.path)} data-testid={`dock-${m.id}`}>
            <Icon name={m.icon} size={15} /><span>{rt.t(m.labelKey)}</span>
          </button>
        ))}
      </div>
      <HelpButton moduleId={current} variant="space" />
    </nav>
  );
}
